import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { authAPI } from '../services/api';

const GoogleOAuth = ({ onSuccess, onError, buttonText = 'signin_with' }) => {
  const [isLoading, setIsLoading] = useState(false);
  const { loginWithGoogle } = useAuth();
  const isInitializedRef = useRef(false);

  const handleCredentialResponse = useCallback(async (response) => {
    setIsLoading(true);
    
    try {
      console.log('🔍 Google OAuth response received:', response);
      
      // Handle both credential string and object formats
      let credential = '';
      if (typeof response === 'string') {
        credential = response;
      } else if (response && response.credential) {
        credential = response.credential;
      } else {
        throw new Error('Invalid response format from Google');
      }

      console.log('🔍 Using credential:', credential.substring(0, 50) + '...');
      
      // Call the API to authenticate with Google
      const googleResponse = await authAPI.googleAuth({ idToken: credential });
      
      // Call the login function from AuthContext with the response
      await loginWithGoogle(googleResponse);
      
      if (onSuccess) {
        onSuccess(googleResponse);
      }
    } catch (error) {
      console.error('❌ Google OAuth error:', error);
      if (onError) {
        onError(error);
      }
    } finally {
      setIsLoading(false);
    }
  }, [loginWithGoogle, onSuccess, onError]);

  useEffect(() => {
    // Only initialize once to prevent re-rendering/shaking
    if (isInitializedRef.current) return;

    // Load Google Identity Services script
    const loadGoogleScript = () => {
      if (window.google && window.google.accounts) {
        // Small delay to ensure DOM is ready
        setTimeout(initializeGoogleAuth, 100);
        return;
      }

      const script = document.createElement('script');
      script.src = 'https://accounts.google.com/gsi/client';
      script.async = true;
      script.defer = true;
      script.onload = () => {
        // Small delay to ensure DOM is ready
        setTimeout(initializeGoogleAuth, 100);
      };
      document.head.appendChild(script);
    };

    const initializeGoogleAuth = () => {
      if (!window.google || !window.google.accounts) return;
      
      // Check if already initialized
      if (isInitializedRef.current) return;
      
      const buttonElement = document.getElementById('google-signin-button');
      if (!buttonElement) {
        // Retry if element not ready yet
        setTimeout(initializeGoogleAuth, 100);
        return;
      }

      // Clear any existing content to prevent duplicates
      buttonElement.innerHTML = '';

      window.google.accounts.id.initialize({
        client_id: process.env.REACT_APP_GOOGLE_CLIENT_ID,
        callback: handleCredentialResponse,
        auto_select: false,
        cancel_on_tap_outside: true,
        // Request additional scopes for Sheets and Calendar
        scope: 'openid email profile https://www.googleapis.com/auth/spreadsheets https://www.googleapis.com/auth/drive.file https://www.googleapis.com/auth/calendar'
      });

      // Render the button
      try {
        window.google.accounts.id.renderButton(buttonElement, {
          theme: 'outline',
          size: 'large',
          text: buttonText,
          shape: 'rectangular'
        });
        isInitializedRef.current = true;
      } catch (error) {
        console.error('Error rendering Google button:', error);
      }
    };

    loadGoogleScript();
  }, [buttonText, handleCredentialResponse]);

  return (
    <div className="w-full">
      <style>
        {`
          #google-signin-button {
            display: flex !important;
            justify-content: center !important;
            min-height: 40px !important;
          }
          #google-signin-button > div {
            margin: 0 auto !important;
          }
        `}
      </style>
      <div className="w-full">
        <div 
          id="google-signin-button" 
          className={`w-full flex justify-center transition-opacity duration-200 ${
            isLoading ? 'opacity-50 pointer-events-none' : 'opacity-100'
          }`}
          style={{
            display: 'flex',
            justifyContent: 'center'
          }}
        />
        {isLoading && (
          <div className="mt-2 flex items-center justify-center">
            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
            <span className="ml-2 text-sm text-[var(--sf-text-secondary)]">Signing in...</span>
          </div>
        )}
      </div>
    </div>
  );
};

export default GoogleOAuth;
